import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import api, { API_BASE_URL } from '../services/api';

function ChildDashboard() {
  const [user, setUser] = useState(null);
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    waiting: 0
  });
  const [wallet, setWallet] = useState({ balance: 0 });
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [note, setNote] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/child/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'child') {
        navigate('/');
        return;
      }
      setUser(parsedUser);
      fetchTodayMissions();
      fetchWalletData(parsedUser.id);
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/child/login');
    }
  }, [navigate]);

  // 화면 복귀 시 자동 새로고침 + 30초 폴링
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) return;
    let parsedUser;
    try { parsedUser = JSON.parse(userData); } catch { return; }

    const refresh = () => {
      fetchTodayMissions();
      fetchWalletData(parsedUser.id);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    const interval = setInterval(refresh, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);

  const fetchWalletData = async (childId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/wallets/${childId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWallet({
          balance: data.wallet.currentBalance || 0
        });
      } else {
        console.error('지갑 데이터 로딩 실패');
      }
    } catch (error) {
      console.error('지갑 데이터 로딩 오류:', error);
    }
  };

  const fetchTodayMissions = async () => {
    try {
      const token = localStorage.getItem('token');
      // 한국 시간대(KST) 기준으로 날짜 계산
      const today = new Date(new Date().getTime() + (9 * 60 * 60 * 1000)).toISOString().split('T')[0];
      const response = await api.get(`/assignments/today?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('API Response:', response.data.assignments);
      setMissions(response.data.assignments);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Fetch missions error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleMissionCheck = (mission) => {
    if (mission.requires_photo) {
      setShowPhotoModal(mission);
      setNote('');
      setSelectedFile(null);
    } else {
      // 사진이 필요 없는 미션은 바로 체크
      checkMission(mission.id, '', null);
    }
  };

  const compressImage = (file, maxSizeKB = 300) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        reject(new Error('이미지 파일만 업로드 가능합니다.'));
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // 최대 크기 설정
        const MAX_WIDTH = 1920;
        const MAX_HEIGHT = 1080;

        let { width, height } = img;

        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
        if (height > MAX_HEIGHT) {
          width = (width * MAX_HEIGHT) / height;
          height = MAX_HEIGHT;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // 품질을 단계적으로 줄여가며 압축
        const tryCompress = (quality) => {
          canvas.toBlob((blob) => {
            if (blob.size <= maxSizeKB * 1024) {
              // 목표 크기 달성
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                resolve({
                  base64,
                  size: blob.size,
                  type: 'image/jpeg'
                });
              };
              reader.readAsDataURL(blob);
            } else if (quality > 0.3) {
              // 품질 더 낮추기
              tryCompress(quality - 0.1);
            } else {
              reject(new Error('이미지가 너무 커서 압축할 수 없습니다.'));
            }
          }, 'image/jpeg', quality);
        };

        tryCompress(0.8);
      };

      img.onerror = () => reject(new Error('이미지를 읽을 수 없습니다.'));

      const reader = new FileReader();
      reader.onload = (e) => img.src = e.target.result;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('File selected:', file.name, (file.size/1024).toFixed(1) + 'KB');
    setImageProcessing(true);

    try {
      const result = await compressImage(file);
      console.log('Compression success:', (result.size/1024).toFixed(1) + 'KB');

      setSelectedFile({
        base64: result.base64,
        name: file.name,
        size: result.size,
        type: result.type
      });
    } catch (error) {
      console.error('Compression error:', error);
      alert(error.message);
      setSelectedFile(null);
    } finally {
      setImageProcessing(false);
      event.target.value = ''; // 파일 입력 초기화
    }
  };

  // Capacitor Camera 플러그인으로 사진 촬영
  const takePhoto = useCallback(async () => {
    setImageProcessing(true);
    try {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt,
        width: 1920,
        height: 1080,
        correctOrientation: true,
        promptLabelHeader: '증빙 사진',
        promptLabelPhoto: '갤러리에서 선택',
        promptLabelPicture: '카메라로 촬영',
        promptLabelCancel: '취소'
      });

      // 이미 base64로 받으므로 압축 처리
      const byteLength = Math.ceil(photo.base64String.length * 3 / 4);
      if (byteLength > 300 * 1024) {
        // Canvas로 추가 압축
        const img = new Image();
        img.src = `data:image/${photo.format};base64,${photo.base64String}`;
        await new Promise((resolve) => { img.onload = resolve; });

        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > 1920) { height = height * 1920 / width; width = 1920; }
        if (height > 1080) { width = width * 1080 / height; height = 1080; }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);

        const blob = await new Promise(resolve =>
          canvas.toBlob(resolve, 'image/jpeg', 0.6)
        );
        const reader = new FileReader();
        const base64 = await new Promise(resolve => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });

        setSelectedFile({ base64, name: 'photo.jpg', size: blob.size, type: 'image/jpeg' });
      } else {
        setSelectedFile({
          base64: photo.base64String,
          name: `photo.${photo.format}`,
          size: byteLength,
          type: `image/${photo.format}`
        });
      }
    } catch (error) {
      if (error.message !== 'User cancelled photos app') {
        console.error('Camera error:', error);
        alert('카메라를 사용할 수 없습니다. 파일 선택을 이용해주세요.');
      }
    } finally {
      setImageProcessing(false);
    }
  }, []);

  const isNativeApp = Capacitor.isNativePlatform();

  const checkMission = async (missionId, missionNote, file) => {
    console.log('checkMission called:', { missionId, missionNote, file });
    setChecking(missionId);

    try {
      const token = localStorage.getItem('token');
      let requestData = { note: missionNote };

      // 파일이 있으면 압축된 이미지 사용
      if (file && file.base64) {
        console.log('Adding compressed image to request');
        requestData.evidence_base64 = file.base64;
        requestData.mime_type = file.type;
      }

      console.log('Submitting mission check with data:', requestData);
      await submitMissionCheck(missionId, requestData, token);
    } catch (error) {
      console.error('Mission check error:', error);
      alert('미션 체크 중 오류가 발생했습니다.');
      setChecking(null);
    }
  };

  const submitMissionCheck = async (missionId, data, token) => {
    try {
      await api.post(`/assignments/${missionId}/check`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('미션이 완료되었습니다! 🎉');
      setShowPhotoModal(null);
      fetchTodayMissions(); // 미션 목록 새로고침
      if (user) {
        fetchWalletData(user.id); // 지갑 정보도 새로고침
      }
    } catch (error) {
      alert(error.response?.data?.error || '미션 체크 중 오류가 발생했습니다.');
    } finally {
      setChecking(null);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '미완료';
      case 'checked': return '확인 대기';
      case 'approved': return '완료';
      case 'rejected': return '다시 하기';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'checked': return '⏰';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      default: return '⏳';
    }
  };

  if (!user) {
    return (
      <div className="dashboard-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* 헤더 */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">🐿️ {user.name}의 도토리</h1>
          <button
            onClick={handleLogout}
            className="btn btn-outline"
            style={{ fontSize: '0.9rem' }}
          >
            로그아웃
          </button>
        </div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="dashboard-content">
        <div className="welcome-section">
          <h2>🌟 안녕하세요, {user.name}님!</h2>
          <p>오늘의 미션을 완료해서 도토리를 모아보세요</p>
        </div>

        {/* 포인트 카드 */}
        <div className="points-card">
          <div className="points-content">
            <div className="acorn-icon"><img src="/icon-512.png" alt="도토리" /></div>
            <div className="points-info">
              <h3>내 도토리</h3>
              <p className="points-amount">{wallet.balance}개</p>
            </div>
            <div className="weekly-preview">
              <p>오늘 완료</p>
              <p className="preview-amount">{stats.completed}/{stats.total}</p>
            </div>
          </div>
        </div>

        {/* 큰 카드형 메뉴 */}
        <div className="menu-section">
          <h3>🎯 메뉴</h3>
          <div className="menu-grid">
            <div className="menu-card" onClick={() => navigate('/child/points')}>
              <div className="menu-icon"><img src="/icon-512.png" alt="도토리" style={{width: '2.5rem', height: '2.5rem', objectFit: 'contain'}} /></div>
              <h4>내 포인트</h4>
              <p>도토리 확인하기</p>
            </div>
            <div className="menu-card" onClick={() => navigate('/child/records')}>
              <div className="menu-icon">📊</div>
              <h4>내 기록</h4>
              <p>완료한 미션 보기</p>
            </div>
            <div className="menu-card" onClick={() => navigate('/child/badges')}>
              <div className="menu-icon">🏆</div>
              <h4>뱃지</h4>
              <p>받은 뱃지 모아보기</p>
            </div>
            <div className="menu-card" onClick={() => navigate('/child/settings')}>
              <div className="menu-icon">⚙️</div>
              <h4>설정</h4>
              <p>알림 및 기타 설정</p>
            </div>
          </div>
        </div>

        {/* 오늘의 미션 */}
        <div className="missions-section">
          <h3>📋 오늘의 미션</h3>

          {loading ? (
            <div className="missions-loading">
              <p>미션을 불러오는 중...</p>
            </div>
          ) : missions.length === 0 ? (
            <div className="missions-empty">
              <div className="empty-icon">🎯</div>
              <p>오늘은 미션이 없어요!</p>
              <p className="empty-subtitle">푹 쉬세요~ 😴</p>
            </div>
          ) : (
            <div className="missions-list">
              {missions.map(mission => (
                <div key={mission.id} className={`mission-item ${mission.status}`}>
                  <div className="mission-header">
                    <h4>{mission.title}</h4>
                    <div className="mission-badges">
                      {mission.is_required && <span className="badge required">필수</span>}
                      {mission.requires_photo && <span className="badge photo">📷</span>}
                    </div>
                  </div>

                  {mission.description && (
                    <p className="mission-description">{mission.description}</p>
                  )}

                  <div className="mission-info">
                    <span className="mission-deadline">⏰ {mission.cutoff_time.substring(0, 5)}까지</span>
                    <span className={`mission-status ${mission.status}`}>
                      {getStatusIcon(mission.status)} {getStatusText(mission.status)}
                    </span>
                  </div>

                  {mission.status === 'pending' && (
                    <button
                      onClick={() => handleMissionCheck(mission)}
                      disabled={checking === mission.id}
                      className="btn btn-primary btn-full mission-check-btn"
                    >
                      {checking === mission.id ? '처리 중...' : '완료하기 ✨'}
                    </button>
                  )}

                  {mission.status === 'rejected' && (
                    <button
                      onClick={() => handleMissionCheck(mission)}
                      disabled={checking === mission.id}
                      className="btn btn-warning btn-full mission-check-btn"
                    >
                      {checking === mission.id ? '처리 중...' : '다시 시도하기 🔄'}
                    </button>
                  )}

                  {mission.note && (
                    <div className="mission-note">
                      <strong>메모:</strong> {mission.note}
                    </div>
                  )}


                  {mission.parent_note && (mission.status === 'approved' || mission.status === 'rejected') && (
                    <div className={`parent-feedback ${mission.status}`}>
                      <strong>{mission.status === 'approved' ? '👍 부모님 메모:' : '💬 반려 사유:'}</strong> {mission.parent_note}
                    </div>
                  )}

                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 사진 업로드 모달 */}
      {showPhotoModal && (
        <div className="modal-overlay" onClick={() => setShowPhotoModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📷 증빙 사진 업로드</h3>
              <button
                onClick={() => setShowPhotoModal(null)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <h4>{showPhotoModal.title}</h4>
              <p>{showPhotoModal.description}</p>

              <div className="form-group">
                <label className="form-label">증빙 사진</label>
                {isNativeApp ? (
                  <>
                    <button
                      type="button"
                      onClick={takePhoto}
                      disabled={imageProcessing}
                      className="btn btn-camera"
                    >
                      {imageProcessing ? '📷 처리 중...' : '📷 사진 촬영 / 갤러리 선택'}
                    </button>
                    {selectedFile && (
                      <div className="photo-preview-info">
                        ✅ {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)}KB)
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="form-input"
                    disabled={imageProcessing}
                  />
                )}
                <small>
                  {imageProcessing ?
                    '📷 이미지 압축 중...' :
                    '이미지가 자동으로 300KB 이하로 압축됩니다.'
                  }
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">메모 (선택)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="미션에 대한 메모를 남겨보세요"
                  className="form-textarea"
                  rows="3"
                  maxLength="500"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowPhotoModal(null)}
                className="btn btn-outline"
              >
                취소
              </button>
              <button
                onClick={() => {
                  console.log('완료하기 버튼 클릭:', { selectedFile, note, imageProcessing, checking });
                  if (!selectedFile && showPhotoModal.requires_photo) {
                    alert('증빙 사진을 선택해주세요.');
                    return;
                  }
                  checkMission(showPhotoModal.id, note, selectedFile);
                }}
                disabled={imageProcessing || checking === showPhotoModal.id}
                className="btn btn-primary"
              >
                {checking === showPhotoModal.id ? '업로드 중...' : imageProcessing ? '이미지 처리 중...' : '완료하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding-bottom: 20px;
        }

        .dashboard-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding: 15px 0;
        }

        .header-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dashboard-title {
          margin: 0;
          color: #764ba2;
          font-size: 1.3rem;
          font-weight: bold;
        }

        .dashboard-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .welcome-section {
          text-align: center;
          margin-bottom: 25px;
          color: white;
        }

        .welcome-section h2 {
          margin-bottom: 10px;
          font-size: 1.8rem;
        }

        .welcome-section p {
          font-size: 1rem;
          opacity: 0.9;
        }

        .points-card {
          background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%);
          border-radius: 20px;
          padding: 25px;
          margin-bottom: 25px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.1);
        }

        .points-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .acorn-icon {
          font-size: 3.5rem;
        }

        .acorn-icon img {
          width: 3.5rem;
          height: 3.5rem;
          object-fit: contain;
        }

        .points-info {
          flex: 1;
          text-align: center;
          color: white;
        }

        .points-info h3 {
          margin: 0 0 5px 0;
          font-size: 1.1rem;
        }

        .points-amount {
          font-size: 2.5rem;
          font-weight: bold;
          margin: 5px 0;
        }

        .points-subtitle {
          font-size: 0.9rem;
          opacity: 0.8;
          margin: 0;
        }

        .weekly-preview {
          text-align: center;
          color: white;
        }

        .weekly-preview p {
          margin: 2px 0;
          font-size: 0.9rem;
        }

        .preview-amount {
          font-weight: bold;
          font-size: 1.2rem;
        }

        .missions-section {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 25px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .missions-section h3 {
          color: #333;
          margin-bottom: 15px;
          text-align: center;
        }

        .missions-loading {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .missions-empty {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 15px;
        }

        .missions-empty p {
          margin: 8px 0;
        }

        .empty-subtitle {
          font-size: 0.9rem;
          opacity: 0.7;
        }

        .missions-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .mission-item {
          border: 2px solid #f0f0f0;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s;
        }

        .mission-item.pending {
          border-color: #ffa500;
          background: #fffaf0;
        }

        .mission-item.checked {
          border-color: #4dabf7;
          background: #f0f8ff;
        }

        .mission-item.approved {
          border-color: #51cf66;
          background: #f0fff4;
        }

        .mission-item.rejected {
          border-color: #ff6b6b;
          background: #fff5f5;
        }

        .mission-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .mission-header h4 {
          margin: 0;
          color: #333;
          font-size: 1.1rem;
        }

        .mission-badges {
          display: flex;
          gap: 5px;
        }

        .badge {
          padding: 4px 8px;
          border-radius: 10px;
          font-size: 0.7rem;
          font-weight: bold;
          color: white;
        }

        .badge.required {
          background: #ff4757;
        }

        .badge.photo {
          background: #feca57;
        }

        .mission-description {
          color: #666;
          font-size: 0.9rem;
          margin: 10px 0;
          line-height: 1.4;
        }

        .mission-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 15px 0;
          font-size: 0.9rem;
        }

        .mission-deadline {
          color: #666;
        }

        .mission-status {
          font-weight: bold;
        }

        .mission-status.pending {
          color: #ff8c00;
        }

        .mission-status.checked {
          color: #1e90ff;
        }

        .mission-status.approved {
          color: #32cd32;
        }

        .mission-status.rejected {
          color: #ff4757;
        }

        .mission-check-btn {
          margin-top: 15px;
          font-weight: bold;
        }

        .mission-note {
          margin-top: 15px;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 8px;
          font-size: 0.9rem;
          color: #555;
        }

        .parent-feedback {
          margin-top: 15px;
          padding: 12px;
          border-radius: 8px;
          font-size: 0.9rem;
          border-left: 4px solid;
        }

        .parent-feedback.approved {
          background: #e8f5e8;
          border-color: #28a745;
          color: #155724;
        }

        .parent-feedback.rejected {
          background: #f8d7da;
          border-color: #dc3545;
          color: #721c24;
        }

        .menu-section {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 25px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .menu-section h3 {
          color: #333;
          margin-bottom: 20px;
          text-align: center;
        }

        .menu-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 15px;
        }

        .menu-card {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          border-radius: 15px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          color: white;
          border: none;
          box-shadow: 0 4px 15px rgba(240, 147, 251, 0.3);
        }

        .menu-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(240, 147, 251, 0.4);
        }

        .menu-card:nth-child(2) {
          background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
          box-shadow: 0 4px 15px rgba(168, 237, 234, 0.3);
        }

        .menu-card:nth-child(2):hover {
          box-shadow: 0 8px 25px rgba(168, 237, 234, 0.4);
        }

        .menu-card:nth-child(3) {
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
          box-shadow: 0 4px 15px rgba(252, 182, 159, 0.3);
        }

        .menu-card:nth-child(3):hover {
          box-shadow: 0 8px 25px rgba(252, 182, 159, 0.4);
        }

        .menu-card:nth-child(4) {
          background: linear-gradient(135deg, #a8caba 0%, #5d4e75 100%);
          box-shadow: 0 4px 15px rgba(168, 202, 186, 0.3);
        }

        .menu-card:nth-child(4):hover {
          box-shadow: 0 8px 25px rgba(168, 202, 186, 0.4);
        }

        .menu-icon {
          font-size: 2.5rem;
          margin-bottom: 10px;
        }

        .menu-card h4 {
          margin: 0 0 5px 0;
          font-size: 1.1rem;
          font-weight: bold;
        }

        .menu-card p {
          margin: 0;
          font-size: 0.85rem;
          opacity: 0.9;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 15px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
        }

        .modal-header h3 {
          margin: 0;
          color: #333;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #666;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-body h4 {
          margin: 0 0 10px 0;
          color: #333;
        }

        .modal-body p {
          color: #666;
          margin-bottom: 20px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          color: #333;
          font-weight: 500;
        }

        .form-input, .form-textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
        }

        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .modal-actions {
          display: flex;
          gap: 15px;
          justify-content: flex-end;
          padding: 20px;
          border-top: 1px solid #e0e0e0;
        }

        .btn-camera {
          width: 100%;
          padding: 16px;
          font-size: 16px;
          font-weight: 600;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          border-radius: 12px;
          cursor: pointer;
        }
        .btn-camera:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .photo-preview-info {
          margin-top: 8px;
          padding: 8px 12px;
          background: #e8f5e9;
          border-radius: 8px;
          color: #2e7d32;
          font-size: 14px;
        }

        @media (max-width: 480px) {
          .points-content {
            flex-direction: column;
            gap: 15px;
          }

          .weekly-preview {
            align-self: stretch;
          }
        }
      `}</style>
    </div>
  );
}

export default ChildDashboard;